import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './aboutpage.css';
import { FaProjectDiagram, FaSyncAlt, FaRocket, FaRobot } from 'react-icons/fa';
import { Link } from "react-router-dom";

const iconMap = {
  FaProjectDiagram: <FaProjectDiagram className="stat-icon" />,
  FaSyncAlt: <FaSyncAlt className="stat-icon" />,
  FaRocket: <FaRocket className="stat-icon" />,
  FaRobot: <FaRobot className="stat-icon" />
};

const About = () => {
  const swiperRef = useRef(null);
  const [popupImage, setPopupImage] = useState(null);
  const [stats, setStats] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [config, setConfig] = useState(null);

  // Load config.json once
  useEffect(() => {
    fetch("/config.json")
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(err => console.error("Failed to load config:", err));
  }, []);

  // Load stats.json
  useEffect(() => {
    fetch("/data.json")
      .then(res => res.json())
      .then(data => setStats(data.stats))
      .catch(err => console.error("Failed to load stats:", err));
  }, []);

  // Fetch milestones when config is ready
  useEffect(() => {
    if (!config) return;

    fetch(`${config.BASE_URL}${config.GET_MILESTONES}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.milestones) {
          setTimelineData(data.milestones);
        } else {
          console.warn("No milestones found");
        }
      })
      .catch(err => console.error("Failed to load milestones:", err));
  }, [config]);

  const openPopup = (imgSrc) => setPopupImage(imgSrc);
  const closePopup = () => setPopupImage(null);

  const clientImages = [
    '/Clients/client1.png',
    '/Clients/client2.png',
    '/Clients/client3.png',
    '/Clients/client4.png',
    '/Clients/client5.png',
    '/Clients/client6.png',
    '/Clients/client7.png'
  ];

  return (
    <div className='about-page-wrapper'>
      {/* --- About Section --- */}
      <div className='about-container'>
        <div className='about-page'>
          <div className='about-image-container'>
            <div className='about-image'>
              <img src='main-image.png' className='image' alt='About Us' />
            </div>
            <div className='small-images'>
              <img src='mira.png' className='small-image' alt='Small 1' />
              <img src='cybira.png' className='small-image' alt='Small 2' />
              <img src='sita.png' className='small-image' alt='Small 3' />
              <img src='mini.png' className='small-image' alt='Small 4' />
            </div>
          </div>
          <div className='about-content'>
            <h2 className='about-heading'>About Us</h2>
             <p>
              This is Praveen Malla, founder of RC (RoboCoupler Techno Solutions Pvt Ltd), which was founded in 2009 as a startup recognized as an Indian Company. The company is registered in India at Innovation Valley, Visakhapatnam, and is a global provider of comprehensive robotics solutions and services that deal with the design, construction and application of robots, as well as computer systems for their control, sensory feedback, and information processing.
            </p>
            <ol>
              <li>Showcase the company's commitment to innovation and research.</li>
              <li>We specialize in training AI algorithms and deploying them into industry.</li>
              <li>Driven by a commitment to excellence, we specialize in deploying automation services.</li>
            </ol>
          </div>
        </div>
      </div>

      {/* --- Timeline Section --- */}
      <div className="journey-section">
        <h2 className="journey-heading">OUR JOURNEY</h2>
        <div className="timeline">
          <Swiper
            ref={swiperRef}
            direction="vertical"
            modules={[Navigation, Pagination]}
            navigation={{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }}
            speed={1600}
            breakpoints={{ 768: { direction: "horizontal" } }}
            className="swiper-container"
          >
            {timelineData.map((item, index) => (
              <SwiperSlide
                key={index}
                style={{ backgroundImage: `url(/Milestone_images/${item.image_path})` }}
                data-year={item.year}
              >
                <div className="swiper-slide-content">
                  <span className="timeline-year">{item.year}</span>
                  <h4 className="timeline-title">Milestone Achieved</h4>
                  <p className="timeline-text">{item.content}</p>
                </div>
              </SwiperSlide>
            ))}
            <div className="swiper-button-prev"></div>
            <div className="swiper-button-next"></div>
            <div className="swiper-pagination"></div>
          </Swiper>
        </div>
      </div>

      {/* --- Data Analysis Section --- */}
      <div className="data-analysis-section">
        <h2 className="data-analysis-heading">DATA ANALYSIS</h2>
        <h4 className="data-analysis-subheading">Data Board For Smart Technology</h4>
        <p>
          Founded on the belief that technology can reshape the world, we create machines backed by 15 years of industry experience.
        </p>
        
        <div className="data-stats">
          {stats.map((stat, index) => (
            <div key={index} className="stat-box" data-aos="fade-up" data-aos-delay={index * 100}>
              <div className="stat-icon-wrapper">
                {iconMap[stat.icon]}
              </div>
              <div className="stat-value">{stat.value}</div>
              <div className="stat-description">{stat.description}</div>
            </div>
          ))}
        </div>

        <Link to="/gallerypage">
          <button className="discover-more-button">Discover More &gt;</button>
        </Link>
      </div>

      {/* --- Clients Section --- */}
      <div className="clients-section">
        <h2 className="journey-heading">HEAR IT FROM OUR CLIENTS</h2>
        <div className="client-scroller">
          {[...clientImages, ...clientImages].map((img, index) => (
            <div key={index} className="client-logo" onClick={() => openPopup(img)}>
              <div className="image-container">
                <img src={img} alt={`Client ${index % clientImages.length + 1}`} className="client-img" loading="lazy" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* --- Image Popup --- */}
      {popupImage && (
        <div className="ab-image-popup" onClick={closePopup}>
          <div className="ab-popup-content">
            <div className="ab-popup-image-container">
              <img src={popupImage} alt="Full size" className="ab-popup-image" />
            </div>
            <button className="ab-about-close-btn" onClick={closePopup}>×</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default About;
